/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Documents` for the Caintra Connect frontend.
 * @module components/CompanyProfileSections/ThirdTab/Documents
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React, { useCallback, useState } from "react";
import "../index.css";
import { useTranslation } from "react-i18next";
import FileDisplay from "../../../modals/ProposalDisplayModal/FileDisplay";
import {
  getAssetFileName,
  getCompanyFieldValue,
  handleFileDownload,
} from "../../../utils/common";

const Documents = ({ company, selectedProfile = 0 }) => {
  const { t } = useTranslation();
  const brochure = getCompanyFieldValue(
    company,
    "brochure",
    selectedProfile,
    null,
  );
  const [brochureAvailable, setBrochureAvailable] = useState(false);
  /** Saves the brochure for the profile currently on screen. */
  const handleBrochureDownload = useCallback(() => {
    handleFileDownload(brochure);
  }, [brochure]);
  const handleAvailabilityChange = useCallback((isAvailable) => {
    setBrochureAvailable(isAvailable);
  }, []);

  if (typeof brochure !== "string" || brochure.trim() === "") {
    return null;
  }

  return (
    <div className="profileSection" hidden={!brochureAvailable}>
      <h2 className="sectionTitle">
        {t("pages.companyProfile.audiovisualMaterials")}
      </h2>
      <div className="gridContainer">
        <div className="gridRow">
          <div className="gridItem">
            <span className="gridLabel">
              {t("pages.companyProfile.brochure")}
            </span>
            <div className="gridValue">
              <FileDisplay
                fileUrl={brochure}
                fileName={getAssetFileName(brochure)}
                onDownload={handleBrochureDownload}
                onAvailabilityChange={handleAvailabilityChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documents;
